import React from 'react';
import styled from 'styled-components/macro';
import { Formik, Form, FieldArray, useFormikContext } from 'formik';
import { showToast } from '@deeptrue-frontend/ui';
import * as Yup from 'yup';
import Conditions from './Conditions';
import { generateCondition, reverseGenerateCondition } from '../utils';

const validationSchema = Yup.object().shape({
  conditions: Yup.array().of(
    Yup.object().shape({
      question: Yup.string().required('Required'),
      logicOperator: Yup.string().required('Required'),
      equals: Yup.string().when('logicOperator', (val: string) => {
        const isRequired = val !== '!empty' && val !== 'empty';
        if (isRequired) {
          return Yup.string().required('Required');
        }
        return Yup.string();
      }),
    })
  ),
});

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const Button = styled.button`
  height: 1.7rem;
  width: 6rem;
  outline: none;
  border: none;
  background-color: transparent !important;
  max-width: 10rem;
  margin-top: 2rem;
  color: rgb(31, 93, 234) !important;
  border: 1px solid rgb(31, 93, 234);
  border-radius: 5px;
  font-weight: 500;
  cursor: pointer;
`;

const Footer = styled.footer`
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e3e5e8;
  height: 4rem;
  padding: 0 1rem;
`;

interface Props {
  questionsList: string[];
  fieldName: string;
}

type LogicOperator =
  | '='
  | '<'
  | '>'
  | '<='
  | '>='
  | 'empty'
  | '!empty'
  | 'contains'
  | '!contains';

type Condition = {
  question: string;
  logicOperator: LogicOperator;
  value: string;
};

const ConditionCreator = ({ questionsList, fieldName }: Props) => {
  const { setFieldValue, getFieldMeta } = useFormikContext();

  const { value }: { value: string } = getFieldMeta(`${fieldName}.visibleIf`);
  const existingValues = reverseGenerateCondition(value);

  const saveConditions = (val: any) => {
    const condition = generateCondition(val.conditions);
    showToast({ type: 'success', title: 'Logic saved!' });
    setFieldValue(`${fieldName}.visibleIf`, condition);
  };

  return (
    <Content>
      <Formik
        initialValues={{
          conditions: existingValues || [],
        }}
        onSubmit={saveConditions}
        validationSchema={validationSchema}
      >
        {({ handleSubmit, values, setFieldValue }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <FieldArray name="conditions">
                {({ remove, push }) => {
                  return (
                    <Conditions
                      values={values.conditions}
                      push={push}
                      remove={remove}
                      questionsList={questionsList}
                      fieldName="conditions"
                      setFieldValue={setFieldValue}
                    />
                  );
                }}
              </FieldArray>
              <Footer>
                <Button type="submit">Save</Button>
              </Footer>
            </Form>
          );
        }}
      </Formik>
    </Content>
  );
};

export default ConditionCreator;
